import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  mergeStyleSets,
} from "@fluentui/react";
import { CaretLeft, Robot, Car as CarIcon, MapPin, Calendar, LockSimple } from "@phosphor-icons/react";
import IconBadge from "../components/common/IconBadge";
import { getPhosphorIcon } from "../utils/iconMap";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { accessService } from "../services/accessService";
import { useNavigate } from "react-router-dom";

// ── Types ─────────────────────────────────────────────
interface Sa2Area { sa2_code: string; sa2_name: string; area_sqkm: number }

interface Kpis {
  total_infringements: number;
  revenue: number;
  avg_aadt: number;
  heavy_vehicle_pct: number;
  financial_year: string;
}

// ── Constants ─────────────────────────────────────────
const COLORS = ["#F59E0B","#38BDF8","#34D399","#FBBF24","#F472B6","#A78BFA","#FB923C","#06B6D4","#E11D48","#8B5CF6","#10B981","#6366F1","#14B8A6","#D946EF"];

const FY_OPTIONS: IDropdownOption[] = [
  { key: "2024-25", text: "2024-25" },
  { key: "2023-24", text: "2023-24" },
  { key: "2022-23", text: "2022-23" },
];

const INFRINGEMENT_TYPE_OPTIONS: IDropdownOption[] = [
  { key: "all", text: "All Types" },
  { key: "Expired Meter", text: "Expired Meter" },
  { key: "No Parking", text: "No Parking" },
  { key: "Overtime", text: "Overtime" },
  { key: "Disabled Zone", text: "Disabled Zone" },
  { key: "Loading Zone", text: "Loading Zone" },
];

const tooltipStyle = { backgroundColor: "#fff", border: "1px solid #E1E1E1", borderRadius: 4, color: "#232323", fontSize: 12 };

// ── Helpers ───────────────────────────────────────────
function fmtNum(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toLocaleString("en-AU");
}
function fmtCurrency(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  if (isNaN(n)) return "-";
  if (Math.abs(n) >= 1_000_000_000) return "$" + (n / 1_000_000_000).toFixed(1) + "B";
  if (Math.abs(n) >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1) + "M";
  if (Math.abs(n) >= 1_000) return "$" + (n / 1_000).toFixed(0) + "K";
  return "$" + n.toLocaleString("en-AU");
}
function fmtPct(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toFixed(1) + "%";
}
function truncLabel(s: string, max = 18): string {
  return s.length > max ? s.slice(0, max) + "..." : s;
}
function fmtAxis(value: number): string {
  if (Math.abs(value) >= 1_000_000_000) return (value / 1_000_000_000).toFixed(1) + "B";
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
  if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(0) + "K";
  return String(value);
}

// ── Styles ───────────────────────────────────────────
const p = boroondaraPalette;
const styles = mergeStyleSets({
  page: { padding: 32, minHeight: "calc(100vh - 52px)", background: "#F5F5F5", "@media (max-width: 768px)": { padding: 16 } } as any,
  container: { maxWidth: 1280, margin: "0 auto" },
  headerRow: { marginBottom: 24 },
  title: { color: p.text, fontWeight: 800 as any, fontSize: 28, letterSpacing: "-0.02em" },
  subtitle: { color: p.text2, marginTop: 6, maxWidth: 780, lineHeight: "1.6", fontSize: 14 },
  chipRow: { display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" } as any,
  chip: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "#fff", border: "1px solid #E1E1E1", borderRadius: 16,
    padding: "4px 12px", fontSize: 12, color: p.text2,
  },
  filterRow: {
    display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap", alignItems: "flex-end",
    background: "#fff", borderRadius: 8, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  } as any,
  filterLabel: { fontSize: 12, fontWeight: 600, color: p.text2, marginBottom: 4 },
  kpiGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 16, marginBottom: 28,
    "@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
  } as any,
  kpiCard: {
    background: "#fff", borderRadius: 10, padding: "20px 18px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 4,
  } as any,
  kpiLabel: { fontSize: 12, color: p.text2, fontWeight: 600 },
  kpiValue: { fontSize: 24, fontWeight: 800 as any, color: p.text },
  kpiSub: { fontSize: 11, color: p.text3 },
  chartsRow: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28,
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  } as any,
  chartCard: {
    background: "#fff", borderRadius: 10, padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  chartTitle: { fontSize: 14, fontWeight: 700, color: p.text, marginBottom: 12 },
  tableWrap: {
    background: "#fff", borderRadius: 10, padding: 20, marginBottom: 28,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflowX: "auto",
  } as any,
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 } as any,
  th: { textAlign: "left" as any, padding: "8px 10px", borderBottom: "2px solid #E1E1E1", color: p.text2, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" as any },
  td: { padding: "7px 10px", borderBottom: "1px solid #F0F0F0", color: p.text, whiteSpace: "nowrap" as any },
  agentCta: {
    background: "linear-gradient(135deg, #DC2626, #F87171)", borderRadius: 12, padding: "24px 28px",
    display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer",
    transition: "transform 0.15s ease", ":hover": { transform: "translateY(-2px)" },
  } as any,
  agentCtaText: { color: "#fff" },
  agentCtaTitle: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  agentCtaSub: { fontSize: 13, opacity: 0.85 },
  agentCtaIcon: { fontSize: 32, color: "#fff", opacity: 0.9 },
  error: { marginBottom: 20 },
});

// ══════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════
export default function TransportParking() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [sa2Areas, setSa2Areas] = useState<Sa2Area[]>([]);
  const [mainData, setMainData] = useState<any[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [comparisonData, setComparisonData] = useState<any[]>([]);
  const [trafficData, setTrafficData] = useState<any[]>([]);

  const [fy, setFy] = useState("2024-25");
  const [sa2, setSa2] = useState("all");
  const [infringementType, setInfringementType] = useState("all");

  // ── Fetch KPIs + SA2 areas ──
  const fetchBase = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [kpiRes, sa2Res] = await Promise.all([
        accessService.get(`/transport/kpis?fy=${fy}`),
        accessService.get("/abs/sa2-areas"),
      ]);
      setKpis(kpiRes.data);
      setSa2Areas(sa2Res.data);
    } catch (err: any) {
      setError(err.message || "Failed to load transport data");
    } finally {
      setLoading(false);
    }
  }, [fy]);

  useEffect(() => { fetchBase(); }, [fetchBase]);

  // ── Fetch main infringement data ──
  const fetchMain = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set("fy", fy);
      if (sa2 !== "all") params.set("sa2", sa2);
      if (infringementType !== "all") params.set("type", infringementType);
      const res = await accessService.get(`/transport/infringements?${params}`);
      setMainData(res.data);
    } catch (err: any) {
      console.error("Infringements error:", err);
    }
  }, [fy, sa2, infringementType]);

  useEffect(() => { fetchMain(); }, [fetchMain]);

  // ── Fetch trend data ──
  const fetchTrend = useCallback(async () => {
    try {
      const params = sa2 !== "all" ? `?sa2=${sa2}` : "";
      const res = await accessService.get(`/transport/trend${params}`);
      setTrendData(res.data);
    } catch (err: any) {
      console.error("Trend error:", err);
    }
  }, [sa2]);

  useEffect(() => { fetchTrend(); }, [fetchTrend]);

  // ── Fetch comparison data ──
  const fetchComparison = useCallback(async () => {
    try {
      const res = await accessService.get(`/transport/comparison?fy=${fy}&metric=revenue`);
      setComparisonData(res.data);
    } catch (err: any) {
      console.error("Comparison error:", err);
    }
  }, [fy]);

  useEffect(() => { fetchComparison(); }, [fetchComparison]);

  // ── Fetch traffic data ──
  const fetchTraffic = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set("fy", fy);
      if (sa2 !== "all") params.set("sa2", sa2);
      const res = await accessService.get(`/transport/traffic?${params}`);
      setTrafficData(res.data);
    } catch (err: any) {
      console.error("Traffic error:", err);
    }
  }, [fy, sa2]);

  useEffect(() => { fetchTraffic(); }, [fetchTraffic]);

  // ── Dropdown options ──
  const sa2Options: IDropdownOption[] = useMemo(() => [
    { key: "all", text: "All Boroondara" },
    ...sa2Areas.map(a => ({ key: a.sa2_code, text: a.sa2_name })),
  ], [sa2Areas]);

  // ── Pie data: Infringements by Type ──
  const typePieData = useMemo(() => {
    if (mainData.length === 0) return [];
    const byType: Record<string, number> = {};
    mainData.forEach(row => {
      const t = row.infringement_type || "Other";
      byType[t] = (byType[t] || 0) + (Number(row.count) || 1);
    });
    return Object.entries(byType).map(([name, value]) => ({ name, value })).filter(d => d.value > 0);
  }, [mainData]);

  // ── Comparison bar data: Revenue by SA2 ──
  const revenueBarData = useMemo(() => {
    if (comparisonData.length === 0) return [];
    return comparisonData.map(row => ({
      sa2_name: row.sa2_name,
      revenue: Number(row.revenue) || 0,
    })).sort((a, b) => b.revenue - a.revenue);
  }, [comparisonData]);

  // ── Traffic bar data: Traffic by Location ──
  const trafficBarData = useMemo(() => {
    if (trafficData.length === 0) return [];
    return trafficData.map(row => ({
      location: row.location || row.sa2_name,
      aadt: Number(row.aadt) || 0,
    })).sort((a, b) => b.aadt - a.aadt).slice(0, 15);
  }, [trafficData]);

  // ── KPI cards ──
  const kpiCards = useMemo(() => {
    if (!kpis) return [];
    return [
      { icon: "Car", color: "#DC2626", label: "Total Infringements", value: fmtNum(kpis.total_infringements), sub: `FY ${kpis.financial_year}` },
      { icon: "Money", color: "#F59E0B", label: "Revenue", value: fmtCurrency(kpis.revenue), sub: `FY ${kpis.financial_year}` },
      { icon: "Road", color: "#38BDF8", label: "Avg AADT", value: fmtNum(kpis.avg_aadt), sub: "average annual daily traffic" },
      { icon: "DeliveryTruck", color: "#6366F1", label: "Heavy Vehicle %", value: fmtPct(kpis.heavy_vehicle_pct), sub: "of total traffic volume" },
    ];
  }, [kpis]);

  // ══════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════
  if (loading && !kpis) {
    return (
      <div className={styles.page}>
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 120 }}>
          <Spinner size={SpinnerSize.large} label="Loading transport & parking data..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* ── Breadcrumb ── */}
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => nav('/dashboards')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: '#DC2626', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <CaretLeft size={10} weight="bold" /> Dashboards
          </button>
        </div>

        {/* ── Header ── */}
        <div className={styles.headerRow}>
          <div className={styles.title}>Transport & Parking</div>
          <div className={styles.subtitle}>
            Parking infringements, traffic counts, revenue and transport data
            across {sa2Areas.length} SA2 areas.
          </div>
          <div className={styles.chipRow}>
            <span className={styles.chip}><CarIcon size={12} weight="regular" /> Transport & Parking</span>
            <span className={styles.chip}><MapPin size={12} weight="regular" /> {sa2Areas.length} SA2 Areas</span>
            <span className={styles.chip}><Calendar size={12} weight="regular" /> FY 2022-23 to 2024-25</span>
            <span className={styles.chip}><LockSimple size={12} weight="regular" /> OFFICIAL: Sensitive</span>
          </div>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={styles.error}>{error}</MessageBar>
        )}

        {/* ── Filters ── */}
        <div className={styles.filterRow}>
          <div>
            <div className={styles.filterLabel}>Financial Year</div>
            <Dropdown
              selectedKey={fy}
              options={FY_OPTIONS}
              onChange={(_, opt) => opt && setFy(String(opt.key))}
              styles={{ root: { width: 160 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>SA2 Area</div>
            <Dropdown
              selectedKey={sa2}
              options={sa2Options}
              onChange={(_, opt) => opt && setSa2(String(opt.key))}
              styles={{ root: { width: 220 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>Infringement Type</div>
            <Dropdown
              selectedKey={infringementType}
              options={INFRINGEMENT_TYPE_OPTIONS}
              onChange={(_, opt) => opt && setInfringementType(String(opt.key))}
              styles={{ root: { width: 200 } }}
            />
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className={styles.kpiGrid}>
          {kpiCards.map((kpi, i) => (
            <div key={i} className={styles.kpiCard}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <IconBadge icon={getPhosphorIcon(kpi.icon)} size="sm" color={kpi.color} weight="fill" />
                <span className={styles.kpiLabel}>{kpi.label}</span>
              </div>
              <div className={styles.kpiValue} style={{ color: kpi.color }}>{kpi.value}</div>
              <div className={styles.kpiSub}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Charts ── */}
        <div className={styles.chartsRow}>
          {/* Pie chart: Infringements by Type */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>
              Infringements by Type ({fy}) {sa2 !== "all" ? `- ${sa2Options.find(o => o.key === sa2)?.text}` : "- All Areas"}
            </div>
            {typePieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie data={typePieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    outerRadius={120} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={true} fontSize={11}>
                    {typePieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No infringement data available
              </div>
            )}
          </div>

          {/* Bar chart: Revenue by SA2 */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>Revenue by SA2 ({fy})</div>
            {revenueBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={revenueBarData} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="sa2_name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v, 14)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="revenue" name="Revenue" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No revenue data available
              </div>
            )}
          </div>
        </div>

        <div className={styles.chartsRow}>
          {/* Bar chart: Traffic by Location */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>
              Traffic by Location (AADT) {sa2 !== "all" ? `- ${sa2Options.find(o => o.key === sa2)?.text}` : "- All Boroondara"}
            </div>
            {trafficBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={trafficBarData} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="location" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v, 14)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="aadt" name="AADT" fill="#38BDF8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No traffic data available
              </div>
            )}
          </div>

          {/* Line chart: Infringements Trend */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>
              Infringements Trend {sa2 !== "all" ? `- ${sa2Options.find(o => o.key === sa2)?.text}` : "- All Boroondara"}
            </div>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="financial_year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtAxis} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Line type="monotone" dataKey="value" name="Infringements" stroke="#DC2626" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No trend data available
              </div>
            )}
          </div>
        </div>

        {/* ── Infringements Table ── */}
        <div className={styles.tableWrap}>
          <div className={styles.chartTitle}>
            Infringement Details — FY {fy}
            {sa2 !== "all" && ` — ${sa2Options.find(o => o.key === sa2)?.text}`}
          </div>
          {mainData.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Suburb</th>
                  <th className={styles.th}>Type</th>
                  <th className={styles.th}>Count</th>
                  <th className={styles.th}>Revenue</th>
                  <th className={styles.th}>FY</th>
                  <th className={styles.th}>Quarter</th>
                </tr>
              </thead>
              <tbody>
                {mainData.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff" }}>
                    <td className={styles.td}>{row.sa2_name}</td>
                    <td className={styles.td}>{row.infringement_type}</td>
                    <td className={styles.td}>{fmtNum(row.count)}</td>
                    <td className={styles.td}>{fmtCurrency(row.revenue)}</td>
                    <td className={styles.td}>{row.financial_year}</td>
                    <td className={styles.td}>{row.quarter}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: p.text3 }}>
              No infringement data available for the selected filters.
            </div>
          )}
        </div>

        {/* ── Agent CTA ── */}
        <div className={styles.agentCta} onClick={() => nav("/chat")} role="button" tabIndex={0}>
          <div className={styles.agentCtaText}>
            <div className={styles.agentCtaTitle}>Ask the Transport & Parking Agent</div>
            <div className={styles.agentCtaSub}>
              Get instant answers about parking infringements, traffic counts, revenue and transport data across Boroondara suburbs.
            </div>
          </div>
          <Robot size={32} weight="duotone" color="#fff" style={{ opacity: 0.9 }} />
        </div>
      </div>
    </div>
  );
}
